import traverse from 'traverse';

import { isPersonDisplayName } from './isPersonDisplayName';
import { DISPLAY_NAME_KEY_REGEX, URL_KEY_REGEX, VALUES_TO_REDACT_REGEX } from './keys';
import { redactUrlParams } from './redactUrlParams';

const MAX_STRING_LENGTH = 512;

function shouldRedactKey(key: string, context: traverse.TraverseContext): boolean {
	return VALUES_TO_REDACT_REGEX.test(key) && (!DISPLAY_NAME_KEY_REGEX.test(key) || isPersonDisplayName(context));
}

/** True for the first half of a UTF-16 surrogate pair, which must not be cut off from its second half. */
function isHighSurrogate(code: number): boolean {
	return code >= 0xd800 && code <= 0xdbff;
}

/** Redacts URL query params under URL-shaped keys, then truncates strings longer than `MAX_STRING_LENGTH`. */
function redactLeafString(key: string | undefined, value: string): string {
	const withUrlParamsRedacted = key && URL_KEY_REGEX.test(key) ? redactUrlParams(value) : value;
	if (withUrlParamsRedacted.length > MAX_STRING_LENGTH) {
		// don't split a surrogate pair (e.g. an emoji) in half
		const end = isHighSurrogate(withUrlParamsRedacted.charCodeAt(MAX_STRING_LENGTH - 1))
			? MAX_STRING_LENGTH - 1
			: MAX_STRING_LENGTH;
		return `${withUrlParamsRedacted.substring(0, end)}[truncated]`;
	}
	return withUrlParamsRedacted;
}

/**
 * Redacts an object for logging/reporting: blanks values whose key matches a known
 * auth/PII pattern, redacts sensitive query params inside string values found under a
 * URL-shaped key (e.g. `url`, `originalUrl`), marks circular references, and truncates
 * long strings.
 */
export function redact<T>(object: T): T {
	// `traverse`'s own typings declare `.map()` as returning `any` regardless of the input type,
	// so the cast to `T` below is required even though the runtime shape is preserved (leaf
	// values may be replaced with redacted/truncated strings, but the overall structure of T is
	// not otherwise changed).
	// eslint-disable-next-line array-callback-return -- traverse's `.map` uses `this.update(...)`, not a return value, to communicate results
	return traverse(object).map(function traverser(this: traverse.TraverseContext) {
		if (this.circular) {
			this.update('[Circular]');
			return;
		}
		if (this.key && shouldRedactKey(this.key, this)) {
			this.update('[Redacted]');
			return;
		}
		if (!this.isLeaf || typeof this.node !== 'string') return;
		const value = redactLeafString(this.key, this.node);
		// oxlint-disable-next-line array-callback-return -- traverse's `.map` uses `this.update(...)`, not a return value, to communicate results
		if (value !== this.node) {
			this.update(value);
		}
	}) as T;
}
